import React, { useEffect, useMemo, useState } from "react";
import style from "css/admin/component.module.css";
import { Modal, Form, Button, ConfigProvider, Select, message } from "antd";
import SearchInput from "component/admin/search/searchInput";
import axios from "axios";

function ProductModal({ isOpen, close, mode = "create", initialData = null, onSuccess, manufacturerList = [] }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const manufacturerOptions = useMemo(() => {
    return (manufacturerList || [])
      .filter((m) => m?.key && m.key !== "ALL")
      .map((m) => ({ value: m.key, label: m.label }));
  }, [manufacturerList]);

  const closeHandler = () => {
    form.resetFields();
    close();
  };

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && initialData) {
      form.setFieldsValue({
        MANUFACTURER_CD: initialData?.MANUFACTURER_CD ?? "",
        PRODUCT_CD: initialData?.PRODUCT_CD ?? "",
        PRODUCT_NM: initialData?.PRODUCT_NM ?? "",
        LICENSE_CNT: initialData?.LICENSE_CNT ?? "",
        IMG_NM: initialData?.IMG_NM ?? "",
        DOWNLOAD_URL: initialData?.DOWNLOAD_URL ?? "",
      });
    } else {
      form.resetFields();
    }
  }, [form, initialData, isOpen, mode]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (mode === "edit") {
        const licenseId = initialData?.LICENSE_ID;
        await axios.put(`/product/${licenseId}`, values);
        message.success("제품이 성공적으로 수정되었습니다.");
      } else {
        await axios.post("/product", values);
        message.success("제품이 성공적으로 등록되었습니다.");
      }

      if (typeof onSuccess === "function") onSuccess();
      closeHandler();
    } catch (err) {
      if (err?.errorFields) {
        message.error("필수 항목을 확인해주세요.");
        return;
      }
      const serverMsg = err?.response?.data?.message || err?.response?.data?.detail || err?.response?.data || err?.message;
      message.error(serverMsg ? `제품 저장 오류: ${serverMsg}` : "제품 저장 중 오류가 발생했습니다.");
      // eslint-disable-next-line no-console
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        <span className={style.searchItem_label} style={{ fontSize: 18 }}>
          {mode === "edit" ? "제품 수정" : "제품 등록"}
        </span>
      }
      open={isOpen}
      onCancel={closeHandler}
      closable={false}
      maskClosable={!saving}
      styles={{ content: { borderRadius: 12 }, mask: { backdropFilter: "none" } }}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Button
              key="cancel"
              onClick={closeHandler}
              disabled={saving}
              style={{ flex: 1, height: 40, borderRadius: 5, fontSize: 18 }}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={submit}
              loading={saving}
              style={{ flex: 1, height: 40, borderRadius: 5, fontSize: 18 }}
            >
              {mode === "edit" ? "수정" : "등록"}
            </Button>
          </div>
        </ConfigProvider>
      }
      centered
    >
      <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 10 }}>
        <Form.Item
          name="MANUFACTURER_CD"
          label={<span className={style.searchItem_label}>제조사</span>}
          rules={[{ required: true, message: "제조사를 선택하세요" }]}
        >
          <Select
            className={style.search_cascader}
            options={manufacturerOptions}
            placeholder="선택해주세요"
          />
        </Form.Item>

        <Form.Item
          name="PRODUCT_CD"
          label={<span className={style.searchItem_label}>제품 코드</span>}
          rules={[{ required: true, message: "제품 코드를 입력하세요" }]}
        >
          <SearchInput placeholder="예) ADOBE_CC" fullWidth />
        </Form.Item>

        <Form.Item
          name="PRODUCT_NM"
          label={<span className={style.searchItem_label}>제품명</span>}
          rules={[{ required: true, message: "제품명을 입력하세요" }]}
        >
          <SearchInput placeholder="예) Adobe Creative Cloud" fullWidth />
        </Form.Item>

        <Form.Item
          name="LICENSE_CNT"
          label={<span className={style.searchItem_label}>라이선스 수량</span>}
          rules={[{ required: true, message: "라이선스 수량을 입력하세요" }]}
        >
          <SearchInput type="number" min="0" placeholder="예) 100" fullWidth />
        </Form.Item>

        <Form.Item name="IMG_NM" label={<span className={style.searchItem_label}>이미지 파일명</span>}>
          <SearchInput placeholder="예) adobe_cc.png" fullWidth />
        </Form.Item>

        <Form.Item name="DOWNLOAD_URL" label={<span className={style.searchItem_label}>다운로드 URL</span>}>
          <SearchInput placeholder="예) https://..." fullWidth />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default ProductModal;


